import { Hidden } from '@mui/material';
import Banner from '@cpg-its/ads.containers.banner';
import Button from '@cpg-its/ads.common.button';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { ConfirmationStatusCode, NewHireConfirmationModel, NewHireConfirmationRedirectModel } from '../models/new-hire-confirmation-models';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { NewHireProps } from '../../../models/arc-epi-props';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { getTimeNewHireConfirmationTime } from '../../../../../global/helpers/date-helpers';
import { Employee } from '../../employment/employment-model';

type Status = 'success' | 'error' | 'info' | 'warning';

interface Props {
	model: NewHireConfirmationModel;
}

function NewHireConfirmationView(props: Props) {

	const { model } = props;
	const newHirePropsLoaded = useSelector<TcsmStates, boolean>(state => state.epiProps?.newHireSettingsState?.contentLoaded);
	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state.epiProps?.newHireSettingsState?.epiContent);
	const currentNYTime = getTimeNewHireConfirmationTime();
	const history = useHistory();
	const { state } = useLocation<NewHireConfirmationRedirectModel>();
	const gpsItem = useGpsType(model?.gpsType);
	const currentEmployee = useSelector<TcsmStates, Employee>(currentState => currentState.employmentState.currentEmployee);
	const name = currentEmployee?.first_name && currentEmployee?.last_name ?
        `${currentEmployee?.first_name} ${currentEmployee.last_name}`
        : currentEmployee?.full_name;
	const maskedCn = currentEmployee?.masked_client_number ?? currentEmployee?.masked_client_nbr;
	const dioApproverState = state?.dioApproverConfirmationPage;


	function RenderLateEnrollmentWarning() {
		// TODO: Adjust condition
		const isLateEnrollment = true;

		if (!isLateEnrollment || !newHireProps?.lateEnrollmentConfirmationWarningMessage) return null;

		return (
			<ContentSpacer>
				<Banner
					banner={{
						type: 'success',
						label: <p><strong>{newHireProps?.lateEnrollmentConfirmationWarningMessage}</strong></p>,
					}} />
			</ContentSpacer>
		);
	}

	function HandleOnClick() {
		history.push(model?.buttonLink);
	}

	if (!newHirePropsLoaded) {
		return <LoadingCircle />;
	}

	const getReplacedBannerMessage = () => {
		return model?.alertDescription.replace('[Full Name]', dioApproverState?.currentForminfo?.submitted_by?.name).replace('[Institution]', dioApproverState?.currentForminfo?.employer?.name).replace('[Location]', `${dioApproverState?.currentForminfo?.employer?.city} - ${dioApproverState?.currentForminfo?.employer?.state}`);
	};

	const getReplacedDescriptionMessage = () => {
		if (dioApproverState?.isDioApproverConfirmationPage && !dioApproverState?.approvedState) {
			return model?.description?.replace('[Member Name]', dioApproverState?.currentForminfo?.employee?.name);
		}
		
		return model?.description?.replace('{date}', currentNYTime)?.replace('{name}', name);
	};

	return (
		<PageContainer>
			<MainContent>
				{
					model?.statusCode !== ConfirmationStatusCode?.NH_MEMBER && (
						<ContentSpacer>
							<h4>{name}</h4>
							<p>{`${newHireProps?.clientNumberLabel}: ${dioApproverState?.isDioApproverConfirmationPage ? dioApproverState?.currentForminfo?.employee?.masked_client_number : maskedCn}`}</p>
						</ContentSpacer>
					)
				}
				<RenderLateEnrollmentWarning />
				{
					state?.apiWarning?.warnings?.length > 0 &&
					<ContentSpacer>
						<Banner
							banner={{
								type: 'warning',
								label: state?.apiWarning?.message && <p><strong>{state?.apiWarning?.message}</strong></p>,
								description:
									<ul>
										{state?.apiWarning?.warnings?.map((errorMessage, key) => {
											return (
												<li key={`${key}-warning`}>
													<p>{errorMessage}</p>
												</li>
											);
										})}
									</ul>,
							}}
						/>
					</ContentSpacer>
				}
				<ContentSpacer>
					<Banner
						banner={{
							type: model?.alertType as Status,
							label: <WysiwygWithReactLink className='input-label' content={ dioApproverState?.isDioApproverConfirmationPage ? getReplacedBannerMessage() : model?.alertDescription } />,
						}}
					/>
				</ContentSpacer>
				<PageHeader headerText={model?.title} description={getReplacedDescriptionMessage()} />
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem.contents} />
					</SideBarContent>
				</Hidden>
				<DividerSection removeDivider >
					<WysiwygWithReactLink content={model?.nextStep?.replace('{clientNumber}', maskedCn)?.replace('{name}', name)} />
				</DividerSection>
				{
					state?.enrolledPlanDto?.health &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.healthPlanEnrolledConfirmationMessage} />
					</DividerSection>
				}
				{
					state?.enrolledPlanDto?.layDc &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.layDcPlanEnrolledConfirmationMessage} />
					</DividerSection>
				}
				{
					state?.enrolledPlanDto?.layDb &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.layDbEnrolledConfirmationMessage} />
					</DividerSection>
				}
				{
					state?.enrolledPlanDto?.clergyDb &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.clergyDbEnrolledConfirmationMessage} />
					</DividerSection>
				}
				{
					state?.enrolledPlanDto?.rsvp &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.rsvpEnrolledConfirmationMessage} />
					</DividerSection>
				}
				{
					state?.enrolledPlanDto?.groupLife &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.groupLifeEnrolledConfirmationMessage} />
					</DividerSection>
				}
				{
					state?.enrolledPlanDto?.disability &&
					<DividerSection removeDivider >
						<WysiwygWithReactLink content={newHireProps?.disabilityPlanEnrolledConfirmationMessage} />
					</DividerSection>
				}
				<ButtonWrapper flexEnd>
					<Button buttonStyle='primary' text={model?.buttonText} backgroundColor='#0000FF' onClick={() => HandleOnClick()} />
				</ButtonWrapper>
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem.contents} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
}


export default NewHireConfirmationView;